import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useWebSocketContext } from '../context/WebSocketContext';
import { useChatContext, ChatProvider } from '../context/ChatContext';
import { API } from '../App';
import AppShell from '../layouts/AppShell';
import InboxLayout from '../layouts/InboxLayout';
import InboxWorkspace from '../layouts/InboxWorkspace';
import ChatSidebar from '../components/ChatSidebar';
import ChatWindow from '../components/ChatWindow';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent } from '../components/ui/sheet';
import { SlidersHorizontal, X, Filter, RefreshCw, Users, Moon, Sun, Instagram } from 'lucide-react';
import { useIsMobile } from '../hooks/useMediaQuery';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import { ToastAction } from '../components/ui/toast';
import { hasPermission, hasAnyPermission } from '../utils/permissionUtils';
import { buildNavigationItems } from '../utils/navigationConfig';
import { cn } from '../lib/utils';
import { useTheme } from '../context/ThemeContext';
import SearchInput from '../components/SearchInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';

const TAB_VALUES = ['all', 'facebook', 'instagram', 'whatsapp'];
const TAB_ALIASES = {
  messenger: 'facebook',
};
// Tabs on which the receiving Instagram account filter applies.
const INSTAGRAM_ACCOUNT_TABS = ['all', 'instagram'];

const normalizeTab = (value) => {
  const normalized = (value || '').toString().trim().toLowerCase();
  if (TAB_ALIASES[normalized]) {
    return TAB_ALIASES[normalized];
  }
  if (TAB_VALUES.includes(normalized)) {
    return normalized;
  }
  return 'all';
};

const parseBooleanParam = (value) => value === '1' || value === 'true';

const parsePageParam = (value) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return 1;
  }
  return parsed;
};

const getUrlState = (search, fallbackTab) => {
  const params = new URLSearchParams(search || '');
  const rawTab = params.get('tab') || params.get('platform') || fallbackTab || 'all';
  const tab = normalizeTab(rawTab);
  const q = (params.get('q') || '').trim();
  const threadId = params.get('threadId') || null;
  const unseen = parseBooleanParam(params.get('unseen'));
  const notReplied = parseBooleanParam(params.get('notReplied'));
  const adLeads = parseBooleanParam(params.get('adLeads'));
  const assignee = params.get('assignee') || 'all';
  const igAccount = params.get('igAccount') || 'all';
  const page = parsePageParam(params.get('page'));

  return {
    q,
    tab,
    threadId,
    unseen,
    notReplied,
    adLeads,
    assignee,
    igAccount,
    page
  };
};

const buildSearchParams = (currentSearch, updates = {}) => {
  const rawSearch = currentSearch?.startsWith('?')
    ? currentSearch.slice(1)
    : currentSearch || '';
  const params = new URLSearchParams(rawSearch);

  if ('q' in updates) {
    const nextQuery = (updates.q || '').trim();
    if (nextQuery) {
      params.set('q', nextQuery);
    } else {
      params.delete('q');
    }
  }

  if ('tab' in updates) {
    const nextTab = normalizeTab(updates.tab);
    params.set('tab', nextTab);
    params.delete('platform');
  }

  if ('threadId' in updates) {
    if (updates.threadId) {
      params.set('threadId', updates.threadId);
    } else {
      params.delete('threadId');
    }
  }

  if ('unseen' in updates) {
    if (updates.unseen) {
      params.set('unseen', '1');
    } else {
      params.delete('unseen');
    }
  }

  if ('notReplied' in updates) {
    if (updates.notReplied) {
      params.set('notReplied', '1');
    } else {
      params.delete('notReplied');
    }
  }

  if ('adLeads' in updates) {
    if (updates.adLeads) {
      params.set('adLeads', '1');
    } else {
      params.delete('adLeads');
    }
  }

  if ('assignee' in updates) {
    if (updates.assignee && updates.assignee !== 'all') {
      params.set('assignee', updates.assignee);
    } else {
      params.delete('assignee');
    }
  }

  if ('igAccount' in updates) {
    if (updates.igAccount && updates.igAccount !== 'all') {
      params.set('igAccount', updates.igAccount);
    } else {
      params.delete('igAccount');
    }
  }

  if ('page' in updates) {
    const nextPage = Number.parseInt(updates.page, 10);
    if (Number.isFinite(nextPage) && nextPage > 1) {
      params.set('page', String(nextPage));
    } else {
      params.delete('page');
    }
  }

  return params;
};

const getChatHandle = (chat) =>
  chat?.instagram_user?.username ||
  chat?.facebook_user?.username ||
  chat?.whatsapp_user?.phone_number ||
  chat?.username ||
  chat?.whatsapp_user_id ||
  chat?.instagram_user_id ||
  '';

const getChatDisplayName = (chat) =>
  chat?.instagram_user?.name ||
  chat?.instagram_user?.username ||
  chat?.facebook_user?.name ||
  chat?.facebook_user?.username ||
  chat?.whatsapp_user?.name ||
  chat?.whatsapp_user?.phone_number ||
  chat?.username ||
  'Unknown';

const InboxContent = ({ user, onLogout }) => {
  const { theme, toggleTheme } = useTheme();
  const { lastMessage, ws } = useWebSocketContext();
  const {
    chats,
    selectedChat,
    loading: chatsLoading,
    error: chatsError,
    loadChats,
    loadMoreChats,
    hasMore,
    isLoadingMore,
    selectChat,
    sendMessage,
    handleWebSocketMessage
  } = useChatContext();
  const { toast } = useToast();

  const { channel } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const lastToastKeyRef = useRef(null);
  const didPromptForNotificationsRef = useRef(false);

  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const pathTab = useMemo(() => normalizeTab(channel || 'all'), [channel]);
  const urlState = useMemo(
    () => getUrlState(location.search, pathTab),
    [location.search, pathTab]
  );
  // URL is the source of truth; draftSearch only tracks in-progress typing.
  const [draftSearch, setDraftSearch] = useState(urlState.q);
  const [pageRestore, setPageRestore] = useState({ target: urlState.page, current: 1 });
  const isRestoringRef = useRef(false);
  const lastFilterKeyRef = useRef('');
  const lastRequestedThreadIdRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileView, setMobileView] = useState('list');
  const [mobileListScrollTop, setMobileListScrollTop] = useState(0);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const lastSearchQueryRef = useRef('');

  const canManageIntegrations = useMemo(() => hasPermission(user, 'integration:manage'), [user]);
  const canManageTemplates = useMemo(() => hasPermission(user, 'template:manage'), [user]);
  const canManagePositions = useMemo(() => hasPermission(user, 'position:manage'), [user]);
  const canManageTeams = useMemo(() => hasPermission(user, 'team:manage'), [user]);
  // Assignment changes ownership of a customer conversation, so it is an
  // administrator-only action even if an agent position has chat:assign.
  const canAssignChats = useMemo(
    () => String(user?.role || '').toLowerCase() === 'admin',
    [user]
  );
  const canLoadAgentDirectory = useMemo(
    () => canAssignChats,
    [canAssignChats]
  );
  const canViewUserRoster = useMemo(
    () => hasAnyPermission(user, ['position:assign', 'position:manage', 'user:manage']),
    [user]
  );
  const canInviteUsers = useMemo(() => hasPermission(user, 'user:invite'), [user]);
  const canViewStats = useMemo(() => hasPermission(user, 'stats:view'), [user]);
  const canModerateComments = useMemo(() => hasPermission(user, 'comment:moderate'), [user]);
  const canViewWhatsAppChats = useMemo(() => hasPermission(user, 'whatsapp:chat:view'), [user]);
  const canViewAllChats = useMemo(
    () =>
      hasPermission(user, 'chat:view:all') ||
      (hasPermission(user, 'chat:view:team') && Boolean(user?.team?.see_all_messages)),
    [user]
  );
  const showAssignmentInfo = useMemo(
    () => canAssignChats || canViewAllChats,
    [canAssignChats, canViewAllChats]
  );

  const navigationItems = useMemo(
    () =>
      buildNavigationItems({
        canManageTemplates,
        canViewUserRoster,
        canManagePositions,
        canManageTeams,
        canInviteUsers,
        canViewStats,
        canManageIntegrations,
        canModerateComments
      }),
    [canManageTemplates, canViewUserRoster, canManagePositions, canManageTeams, canInviteUsers, canViewStats, canManageIntegrations, canModerateComments]
  );

  const activeFilters = useMemo(
    () => ({
      unseen: urlState.unseen,
      notReplied: urlState.notReplied,
      adLeads: urlState.adLeads,
      assignedTo: urlState.assignee || 'all',
      instagramAccount: INSTAGRAM_ACCOUNT_TABS.includes(urlState.tab) ? urlState.igAccount : 'all'
    }),
    [urlState.unseen, urlState.notReplied, urlState.adLeads, urlState.assignee, urlState.tab, urlState.igAccount]
  );

  const buildChatFilterParams = useCallback((filters, query) => {
    const params = {};
    if (filters?.unseen) {
      params.unseen = true;
    }
    if (filters?.notReplied) {
      params.not_replied = true;
    }
    if (filters?.adLeads) {
      params.ad_lead = true;
    }
    if (filters?.assignedTo && filters.assignedTo !== 'all') {
      params.assigned_to = filters.assignedTo;
    }
    if (filters?.instagramAccount && filters.instagramAccount !== 'all') {
      params.inbox_account_id = filters.instagramAccount;
    }
    if (query) {
      params.q = query;
    }
    return params;
  }, []);

  const chatFilterParams = useMemo(
    () => buildChatFilterParams(activeFilters, urlState.q),
    [buildChatFilterParams, activeFilters, urlState.q]
  );

  // Centralized URL updater to avoid ad-hoc query param mutations.
  const setUrlState = useCallback(
    (updates, options = {}) => {
      const nextParams = buildSearchParams(location.search, updates);
      const nextSearch = nextParams.toString();
      const nextPathname = options.pathname || location.pathname;
      const currentSearch = location.search.startsWith('?')
        ? location.search.slice(1)
        : location.search;

      if (nextPathname === location.pathname && nextSearch === currentSearch) {
        return;
      }

      navigate(
        {
          pathname: nextPathname,
          search: nextSearch ? `?${nextSearch}` : ''
        },
        { replace: options.replace ?? false }
      );
    },
    [location.pathname, location.search, navigate]
  );

  const filterKey = useMemo(
    () =>
      `${urlState.tab}|${urlState.q}|${activeFilters.unseen ? 1 : 0}|${
        activeFilters.notReplied ? 1 : 0
      }|${activeFilters.adLeads ? 1 : 0}|${activeFilters.assignedTo}|${activeFilters.instagramAccount}`,
    [urlState.tab, urlState.q, activeFilters.unseen, activeFilters.notReplied, activeFilters.adLeads, activeFilters.assignedTo, activeFilters.instagramAccount]
  );

  const currentTab = urlState.tab;

  const messageFilters = useMemo(
    () => {
      const platformOf = (chat) => String(chat?.platform || '').toLowerCase();
      const loadedFacebookChats = chats.filter((chat) => platformOf(chat) === 'facebook').length;
      const loadedInstagramChats = chats.filter((chat) => platformOf(chat) === 'instagram').length;
      const loadedWhatsAppChats = chats.filter((chat) => platformOf(chat) === 'whatsapp').length;
      const filters = [
        { id: 'all', label: 'All messages', metric: stats?.total_chats ?? chats.length },
        { id: 'facebook', label: 'Messenger', metric: stats?.facebook_chats ?? loadedFacebookChats },
        { id: 'instagram', label: 'Instagram', metric: stats?.instagram_chats ?? loadedInstagramChats }
      ];
      if (canViewWhatsAppChats) {
        filters.push({ id: 'whatsapp', label: 'WhatsApp', metric: stats?.whatsapp_chats ?? loadedWhatsAppChats });
      }
      return filters;
    },
    [stats, chats, canViewWhatsAppChats]
  );

  useEffect(() => {
    if (lastMessage) {
      handleWebSocketMessage(lastMessage);
    }
  }, [lastMessage, handleWebSocketMessage]);

  // Process every socket event directly as well as retaining the latest event
  // for UI notifications. A state-only `lastMessage` handoff can coalesce two
  // rapid events into one React render, which would leave a message and its
  // unread count out of the sidebar until the next reload.
  useEffect(() => ws.subscribe(handleWebSocketMessage), [ws.subscribe, handleWebSocketMessage]);

  useEffect(() => {
    if (!lastMessage || lastMessage.type !== 'new_message') {
      return;
    }
    const sender = (lastMessage.message?.sender || '').toString().toLowerCase();
    if (sender === 'agent' || sender === 'instagram_page') {
      return;
    }
    const messageId =
      lastMessage.message?.id ||
      lastMessage.message?.timestamp ||
      lastMessage.message?.ts ||
      '';
    const toastKey = `${lastMessage.chat_id || 'chat'}:${messageId}`;
    if (lastToastKeyRef.current === toastKey) {
      return;
    }
    lastToastKeyRef.current = toastKey;
    const chat = chats.find((entry) => entry.id === lastMessage.chat_id);
    const rawDisplayName = chat ? getChatDisplayName(chat) : '';
    const displayName =
      rawDisplayName && rawDisplayName !== 'Unknown' ? rawDisplayName : '';
    const handle = chat ? getChatHandle(chat) : '';
    const content = (lastMessage.message?.content || '').trim();
    const descriptionText = content && content.toLowerCase() !== '[attachment]'
      ? content
      : 'New attachment received.';
    const handleLabel = handle ? `@${handle}` : '';
    const description = handleLabel ? `${handleLabel} - ${descriptionText}` : descriptionText;
    const avatarUrl = chat?.profile_pic_url;
    const initials = (displayName || handle || 'U').charAt(0).toUpperCase();
    const icon = (
      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[var(--tg-agent-gradient-start)] to-[var(--tg-agent-gradient-end)] p-[2px]">
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-[var(--tg-surface)]">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={displayName || 'Chat avatar'}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-[11px] font-semibold text-[var(--tg-text-secondary)]">
              {initials}
            </span>
          )}
        </div>
      </div>
    );
    const canUseDesktopNotifications =
      typeof window !== 'undefined' &&
      'Notification' in window &&
      (window.isSecureContext || window.location.hostname === 'localhost');
    const canShowDesktopNotification =
      canUseDesktopNotifications && Notification.permission === 'granted';
    const hasFocus = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
    if (canShowDesktopNotification && (document.visibilityState !== 'visible' || !hasFocus)) {
      try {
        new Notification(
          displayName ? `New message from ${displayName}` : 'New message received',
          {
            body: description,
            tag: toastKey,
            icon: chat?.profile_pic_url || undefined,
          }
        );
      } catch (error) {
        console.warn('Failed to show desktop notification:', error);
      }
    }
    const shouldOfferPermission =
      canUseDesktopNotifications &&
      Notification.permission === 'default' &&
      !didPromptForNotificationsRef.current;
    const action = shouldOfferPermission ? (
      <ToastAction
        altText="Enable desktop notifications"
        className="h-8 rounded-full border border-[var(--tg-border-soft)] bg-[var(--tg-surface-muted)] px-3 text-[11px] font-semibold text-[var(--tg-text-secondary)] hover:bg-[var(--tg-accent-soft)] hover:text-[var(--tg-text-primary)]"
        onClick={async () => {
          didPromptForNotificationsRef.current = true;
          try {
            await Notification.requestPermission();
          } catch (error) {
            console.warn('Notification permission request failed:', error);
          }
        }}
      >
        Enable
      </ToastAction>
    ) : undefined;
    if (shouldOfferPermission) {
      didPromptForNotificationsRef.current = true;
    }
    toast({
      variant: 'notification',
      title: displayName || 'New message',
      description,
      icon,
      action,
    });
  }, [lastMessage, chats, toast]);

  const handleFilterToggle = useCallback(
    (key) => {
      if (key === 'unseen') {
        setUrlState({ unseen: !activeFilters.unseen, page: 1, tab: currentTab });
      }
      if (key === 'notReplied') {
        setUrlState({ notReplied: !activeFilters.notReplied, page: 1, tab: currentTab });
      }
      if (key === 'adLeads') {
        setUrlState({ adLeads: !activeFilters.adLeads, page: 1, tab: currentTab });
      }
    },
    [activeFilters.unseen, activeFilters.notReplied, activeFilters.adLeads, currentTab, setUrlState]
  );

  const handleAssignedFilterChange = useCallback(
    (value) => {
      setUrlState({ assignee: value, page: 1, tab: currentTab });
    },
    [currentTab, setUrlState]
  );

  const handleInstagramAccountFilterChange = useCallback(
    (value) => {
      setUrlState({ igAccount: value, page: 1, tab: currentTab });
    },
    [currentTab, setUrlState]
  );

  const handleClearFilters = useCallback(() => {
    setUrlState({ unseen: false, notReplied: false, adLeads: false, assignee: 'all', igAccount: 'all', page: 1, tab: currentTab });
  }, [currentTab, setUrlState]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/inbox/instagram-accounts`, { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 })
      .then((response) => {
        if (!cancelled) {
          setInstagramAccounts(Array.isArray(response.data) ? response.data : []);
        }
      })
      .catch((err) => {
        // The account filter is optional; the inbox works without it.
        console.error('Error loading Instagram accounts:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMeta = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const headers = { Authorization: `Bearer ${token}` };
      const timeout = 30000;
      const axiosConfig = { headers, timeout };

      const [statsRes, agentsRes] = await Promise.all([
        canViewStats
          ? axios.get(`${API}/dashboard/stats`, axiosConfig)
          : Promise.resolve({ data: null }),
        canLoadAgentDirectory ? axios.get(`${API}/users/agents`, axiosConfig) : Promise.resolve({ data: [] })
      ]);

      setStats(statsRes.data);
      setAgents(agentsRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load data');
      if (err.response?.status === 401) {
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [canLoadAgentDirectory, canViewStats, onLogout]);

  const refreshInbox = useCallback(async () => {
    await loadMeta();
    try {
      await loadChats(currentTab, chatFilterParams);
      setPageRestore({ target: urlState.page, current: 1 });
    } catch (err) {
      console.error('Error refreshing chats:', err);
    }
  }, [loadMeta, loadChats, currentTab, chatFilterParams, urlState.page]);

  // WebSockets are a low-latency hint, not a durable queue. Reconcile after a
  // connection is (re)opened and whenever the tab becomes visible so messages
  // received during a browser/network suspension appear without a manual
  // refresh and their unread badges are restored from the server.
  const reconcileLiveInbox = useCallback(async () => {
    if (document.visibilityState === 'hidden') return;
    try {
      await loadChats(currentTab, chatFilterParams);
    } catch (err) {
      console.warn('Could not reconcile inbox after WebSocket activity:', err);
    }
  }, [loadChats, currentTab, chatFilterParams]);

  useEffect(() => {
    if (ws.isConnected) reconcileLiveInbox();
  }, [ws.isConnected, reconcileLiveInbox]);

  useEffect(() => {
    const reconcileWhenVisible = () => {
      if (document.visibilityState === 'visible') reconcileLiveInbox();
    };
    window.addEventListener('focus', reconcileWhenVisible);
    window.addEventListener('online', reconcileWhenVisible);
    document.addEventListener('visibilitychange', reconcileWhenVisible);
    return () => {
      window.removeEventListener('focus', reconcileWhenVisible);
      window.removeEventListener('online', reconcileWhenVisible);
      document.removeEventListener('visibilitychange', reconcileWhenVisible);
    };
  }, [reconcileLiveInbox]);

  useEffect(() => {
    setDraftSearch(urlState.q);
  }, [urlState.q]);

  // Search is server-side, so wait for a short pause before changing the URL
  // and causing a new cursor query. Enter still submits immediately. Keeping
  // one- and two-character searches explicit avoids costly broad matches.
  useEffect(() => {
    const query = draftSearch.trim();
    if (query === urlState.q || (query.length > 0 && query.length < 3)) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setUrlState({ q: query, page: 1, tab: currentTab }, { replace: true });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [currentTab, draftSearch, setUrlState, urlState.q]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const needsTab = !params.has('tab') || params.has('platform');
    if (needsTab) {
      setUrlState({ tab: currentTab }, { replace: true });
    }
  }, [location.search, currentTab, setUrlState]);

  useEffect(() => {
    const nextPath = currentTab === 'all' ? '/inbox' : `/inbox/${currentTab}`;
    if (location.pathname !== nextPath) {
      navigate({ pathname: nextPath, search: location.search }, { replace: true });
    }
  }, [currentTab, location.pathname, location.search, navigate]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  // Refetch threads only when filter signature changes (not when page changes).
  useEffect(() => {
    if (lastFilterKeyRef.current === filterKey) {
      return;
    }
    lastFilterKeyRef.current = filterKey;
    let cancelled = false;
    const run = async () => {
      try {
        await loadChats(currentTab, chatFilterParams);
      } catch (err) {
        console.error('Error loading chats:', err);
      }
      if (!cancelled) {
        setPageRestore({ target: urlState.page, current: 1 });
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [filterKey, currentTab, chatFilterParams, loadChats, urlState.page]);

  const isRestoring = pageRestore.target > 1 && pageRestore.current < pageRestore.target;

  useEffect(() => {
    isRestoringRef.current = isRestoring;
  }, [isRestoring]);

  // Replay loadMore calls to restore pagination from the URL.
  useEffect(() => {
    if (!isRestoring) {
      return;
    }
    if (!hasMore) {
      setPageRestore((prev) => ({ ...prev, target: prev.current }));
      if (urlState.page !== pageRestore.current) {
        setUrlState({ page: pageRestore.current, tab: currentTab }, { replace: true });
      }
      return;
    }
    if (isLoadingMore) {
      return;
    }
    let cancelled = false;
    const restoreNextPage = async () => {
      const newChats = await loadMoreChats();
      if (cancelled) {
        return;
      }
      if (!newChats.length) {
        setPageRestore((prev) => ({ ...prev, target: prev.current }));
        if (urlState.page !== pageRestore.current) {
          setUrlState({ page: pageRestore.current, tab: currentTab }, { replace: true });
        }
        return;
      }
      setPageRestore((prev) => ({ ...prev, current: prev.current + 1 }));
    };
    restoreNextPage();
    return () => {
      cancelled = true;
    };
  }, [
    isRestoring,
    isLoadingMore,
    hasMore,
    loadMoreChats,
    setUrlState,
    urlState.page,
    pageRestore.current,
    currentTab
  ]);

  useEffect(() => {
    if (!isMobile) {
      setMobileView('list');
      return;
    }
    if (urlState.threadId) {
      setMobileView('thread');
    } else {
      setMobileView('list');
    }
  }, [isMobile, urlState.threadId]);

  const handlePlatformChange = useCallback(
    (platform) => {
      const nextTab = normalizeTab(platform);
      const path = nextTab === 'all' ? '/inbox' : `/inbox/${nextTab}`;
      setUrlState({ tab: nextTab, page: 1, threadId: null }, { pathname: path });
    },
    [setUrlState]
  );

  const handleSearchSubmit = useCallback(() => {
    setUrlState({ q: draftSearch.trim(), page: 1, tab: currentTab });
  }, [draftSearch, currentTab, setUrlState]);

  const handleSearchClear = useCallback(() => {
    setDraftSearch('');
    setUrlState({ q: '', page: 1, tab: currentTab });
  }, [currentTab, setUrlState]);

  const handleSelectChat = useCallback(
    async (chatId) => {
      if (!chatId) {
        return;
      }
      // Select from the row click itself. Depending only on the URL effect
      // left the prior conversation visible while navigation settled, and a
      // second tap could be treated as an already-requested thread.
      lastRequestedThreadIdRef.current = String(chatId);
      setUrlState({ threadId: chatId });
      if (isMobile) {
        setMobileView('thread');
      }
      try {
        await selectChat(chatId);
      } catch (error) {
        console.error('Failed to open selected chat:', error);
      }
    },
    [isMobile, selectChat, setUrlState]
  );

  const handleBackToList = useCallback(() => {
    lastRequestedThreadIdRef.current = null;
    setUrlState({ threadId: null });
    setMobileView('list');
  }, [setUrlState]);

  const handleLoadMore = useCallback(async () => {
    const newChats = await loadMoreChats();
    if (newChats.length && !isRestoringRef.current) {
      setUrlState({ page: urlState.page + 1, tab: currentTab }, { replace: true });
    }
  }, [loadMoreChats, setUrlState, urlState.page, currentTab]);

  // Open the thread from the URL; fall back if it's invalid.
  useEffect(() => {
    const targetId = urlState.threadId ? String(urlState.threadId) : null;
    if (!targetId) {
      lastRequestedThreadIdRef.current = null;
      return;
    }
    const selectedId = selectedChat?.id != null ? String(selectedChat.id) : null;
    if (selectedId === targetId) {
      lastRequestedThreadIdRef.current = targetId;
      return;
    }
    if (lastRequestedThreadIdRef.current === targetId) {
      return;
    }
    lastRequestedThreadIdRef.current = targetId;
    let cancelled = false;
    const openThread = async () => {
      try {
        await selectChat(targetId);
      } catch (error) {
        if (cancelled) {
          return;
        }
        const status = error?.response?.status;
        const shouldFallback = status === 404 || status === 400;
        if (!shouldFallback) {
          return;
        }
        const fallbackId = chats[0]?.id;
        if (fallbackId && String(fallbackId) !== targetId) {
          setUrlState({ threadId: fallbackId }, { replace: true });
        } else {
          setUrlState({ threadId: null }, { replace: true });
        }
      }
    };
    openThread();
    return () => {
      cancelled = true;
    };
  }, [urlState.threadId, selectedChat?.id, selectChat, chats, setUrlState]);

  const handleSendMessage = async (chatId, content) => {
    try {
      await sendMessage(chatId, content);
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  const handleAssignChat = async (chatId, agentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/chats/${chatId}/assign`,
        { agent_id: agentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadChats(currentTab, chatFilterParams);
      if (selectedChat?.id === chatId) {
        try {
          await selectChat(chatId);
        } catch (error) {
          console.warn('Failed to refresh assigned chat:', error);
        }
      }
    } catch (err) {
      console.error('Error assigning chat:', err);
    }
  };

  const renderFilterChips = () => (
    <div className="flex flex-wrap items-center gap-2">
      {messageFilters.map((filter) => {
        const isSelected = currentTab === filter.id;
        return (
          <button
            key={filter.id}
            type="button"
            disabled={filter.disabled}
            onClick={() => handlePlatformChange(filter.id)}
            className={cn(
              'inbox-filter-pill',
              filter.disabled && 'inbox-filter-pill--disabled',
              isSelected && 'inbox-filter-pill--active'
            )}
          >
            <span>{filter.label}</span>
            {typeof filter.metric === 'number' && (
              <span className="inbox-filter-pill__metric">{filter.metric}</span>
            )}
            {filter.badge && <span className="inbox-filter-pill__badge">{filter.badge}</span>}
          </button>
        );
      })}
      {isMobile && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setFilterSheetOpen(true)}
          className="rounded-full border-[var(--tg-border-soft)] bg-[var(--tg-surface)] text-[var(--tg-text-primary)] gap-2"
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters
        </Button>
      )}
    </div>
  );

  const renderToggleChip = (key, label) => (
    <label className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-[var(--tg-border-soft)] bg-[var(--tg-surface-muted)] text-[12px] sm:text-[13px] font-medium text-[var(--tg-text-secondary)]">
      <span>{label}</span>
      <Switch
        checked={activeFilters[key]}
        onCheckedChange={() => handleFilterToggle(key)}
      />
    </label>
  );

  const renderAgentSelect = () =>
    canViewAllChats ? (
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4 text-[var(--tg-text-muted)]" />
        <Select value={activeFilters.assignedTo} onValueChange={handleAssignedFilterChange}>
          <SelectTrigger className="min-w-[150px] sm:min-w-[180px] h-9 bg-[var(--tg-surface)] border-[var(--tg-border-soft)] text-sm rounded-full px-3">
            <SelectValue placeholder="All agents" />
          </SelectTrigger>
          <SelectContent className="bg-[var(--tg-surface)] text-[var(--tg-text-primary)]">
            <SelectItem value="all">All agents</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {agents.map((agent) => (
              <SelectItem key={agent.id} value={agent.id} disabled={!agent.is_active}>
                {agent.name} {agent.is_active ? '' : '(inactive)'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    ) : null;

  const showInstagramAccountFilter = instagramAccounts.length > 1 && INSTAGRAM_ACCOUNT_TABS.includes(currentTab);

  const renderInstagramAccountOptions = () => (
    <SelectContent className="bg-[var(--tg-surface)] text-[var(--tg-text-primary)]">
      <SelectItem value="all">All Instagram accounts</SelectItem>
      {instagramAccounts.map((account) => (
        <SelectItem key={account.id} value={account.id}>
          @{account.name}
        </SelectItem>
      ))}
    </SelectContent>
  );

  const renderInstagramAccountSelect = () =>
    showInstagramAccountFilter ? (
      <div className="flex items-center gap-2">
        <Instagram className="w-4 h-4 text-[var(--tg-text-muted)]" />
        <Select value={activeFilters.instagramAccount} onValueChange={handleInstagramAccountFilterChange}>
          <SelectTrigger
            aria-label="Instagram account"
            className="min-w-[150px] sm:min-w-[180px] h-9 bg-[var(--tg-surface)] border-[var(--tg-border-soft)] text-sm rounded-full px-3"
          >
            <SelectValue placeholder="All Instagram accounts" />
          </SelectTrigger>
          {renderInstagramAccountOptions()}
        </Select>
      </div>
    ) : null;

  const renderFilters = () => (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar py-1">
            {renderFilterChips()}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] hover:bg-[var(--tg-surface-muted)]"
            onClick={() => setFiltersExpanded((prev) => !prev)}
            aria-label="Toggle filters"
            title="Filters"
          >
            <Filter className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="inbox-theme-toggle h-9 w-9 rounded-full border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] hover:bg-[var(--tg-surface-muted)]"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] hover:bg-[var(--tg-surface-muted)]"
            onClick={refreshInbox}
            aria-label="Refresh chats"
            title="Refresh chats"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {filtersExpanded && (
        <div className="flex flex-wrap items-center gap-2">
          {renderToggleChip('unseen', 'Unseen')}
          {renderToggleChip('notReplied', 'Needs reply')}
          {renderToggleChip('adLeads', 'Ad leads')}
          {renderAgentSelect()}
          {renderInstagramAccountSelect()}
        </div>
      )}
    </div>
  );

  const showError = error || chatsError;

  const mobileWorkspace = (
    <div className="flex-1 min-h-0 flex flex-col gap-2">
      {mobileView === 'list' ? (
        <div className="min-h-0 overflow-hidden rounded-2xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] flex-1">
          <ChatSidebar
            chats={chats}
            selectedChatId={null}
            onSelectChat={handleSelectChat}
            selectedPlatform={currentTab}
            loading={chatsLoading}
            hideHeader={false}
            searchQuery={urlState.q}
            searchDraft={draftSearch}
            onSearchDraftChange={setDraftSearch}
            onSearchSubmit={handleSearchSubmit}
            onSearchClear={handleSearchClear}
            showAssignmentInfo={showAssignmentInfo}
            onRefresh={refreshInbox}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={handleLoadMore}
            initialScrollTop={mobileListScrollTop}
            onListScroll={setMobileListScrollTop}
          />
        </div>
      ) : (
        <div className="min-h-0 overflow-hidden rounded-2xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] flex-1">
          <ChatWindow
            chat={selectedChat}
            onSendMessage={handleSendMessage}
            onAssignChat={handleAssignChat}
            agents={agents}
            userRole={user.role}
            canAssignChats={canAssignChats}
            showAssignmentInfo={showAssignmentInfo}
            currentUser={user}
            onBackToList={handleBackToList}
          />
        </div>
      )}
    </div>
  );

  const workspace = isMobile ? (
    mobileWorkspace
  ) : (
    <InboxWorkspace
      className="flex-1 min-h-0"
      listColumn={
        <div className="flex flex-col h-full min-h-0 gap-2.5">
          <SearchInput
            value={draftSearch}
            onChange={setDraftSearch}
            onSubmit={handleSearchSubmit}
            onClear={handleSearchClear}
            placeholder="Search conversations..."
            inputClassName="inbox-search-input inbox-search-input--compact w-full h-10 text-sm"
            buttonVariant="icon"
            className="w-full"
            inputProps={{ 'aria-label': 'Search conversations' }}
          />
          <div className="flex-1 min-h-0">
            <ChatSidebar
              chats={chats}
              selectedChatId={selectedChat?.id}
              onSelectChat={handleSelectChat}
              selectedPlatform={currentTab}
              loading={chatsLoading}
              hideHeader
              searchQuery={urlState.q}
              searchDraft={draftSearch}
              onSearchDraftChange={setDraftSearch}
              onSearchSubmit={handleSearchSubmit}
              onSearchClear={handleSearchClear}
              showAssignmentInfo={showAssignmentInfo}
              onRefresh={refreshInbox}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              onLoadMore={handleLoadMore}
            />
          </div>
        </div>
        }
        conversationColumn={
          <div className="rounded-2xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] h-full flex flex-1 overflow-hidden">
          <ChatWindow
            chat={selectedChat}
            onSendMessage={handleSendMessage}
            onAssignChat={handleAssignChat}
            agents={agents}
            userRole={user.role}
            canAssignChats={canAssignChats}
            showAssignmentInfo={showAssignmentInfo}
            currentUser={user}
          />
        </div>
      }
    />
  );

  return (
    <AppShell user={user} navItems={navigationItems} onLogout={onLogout}>
      <InboxLayout
        statsSection={
          showError ? (
            <div className="rounded-2xl border border-red-200/40 bg-red-500/5 px-3 py-3 flex items-center justify-between">
              <span className="text-sm text-red-200">{showError}</span>
              <button
                onClick={() => {
                  setError(null);
                  refreshInbox();
                }}
                className="text-sm font-semibold text-[var(--tg-accent-strong)] hover:underline"
              >
                Retry
              </button>
            </div>
          ) : loading && !stats ? (
            <div className="rounded-2xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] px-3 py-4 flex items-center justify-center">
              <div className="h-8 w-8 rounded-full border-t-2 border-b-2 border-purple-500 animate-spin" />
            </div>
          ) : null
        }
        filterSection={renderFilters()}
      >
        {workspace}
      </InboxLayout>

      <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
        <SheetContent
          side="bottom"
          className="w-full max-w-full sm:max-w-md mx-auto bg-[var(--tg-surface)] border-t border-[var(--tg-border-soft)] p-6 space-y-5"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </div>
            <Button variant="ghost" size="icon" onClick={() => setFilterSheetOpen(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between px-3 py-2.5 rounded-2xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface-muted)]">
              <span className="text-sm font-medium text-[var(--tg-text-primary)]">Unseen only</span>
              <Switch checked={activeFilters.unseen} onCheckedChange={() => handleFilterToggle('unseen')} />
            </div>
            <div className="flex items-center justify-between px-3 py-2.5 rounded-2xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface-muted)]">
              <span className="text-sm font-medium text-[var(--tg-text-primary)]">Needs reply</span>
              <Switch checked={activeFilters.notReplied} onCheckedChange={() => handleFilterToggle('notReplied')} />
            </div>
            <div className="flex items-center justify-between px-3 py-2.5 rounded-2xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface-muted)]">
              <span className="text-sm font-medium text-[var(--tg-text-primary)]">Ad leads</span>
              <Switch checked={activeFilters.adLeads} onCheckedChange={() => handleFilterToggle('adLeads')} />
            </div>
            {showInstagramAccountFilter && (
              <div className="space-y-2">
                <span className="text-xs uppercase tracking-wide text-[var(--tg-text-muted)]">Instagram account</span>
                <Select value={activeFilters.instagramAccount} onValueChange={handleInstagramAccountFilterChange}>
                  <SelectTrigger className="w-full bg-[var(--tg-surface)] border-[var(--tg-border-soft)] text-sm">
                    <SelectValue placeholder="All Instagram accounts" />
                  </SelectTrigger>
                  {renderInstagramAccountOptions()}
                </Select>
              </div>
            )}
            {canViewAllChats && (
              <div className="space-y-2">
                <span className="text-xs uppercase tracking-wide text-[var(--tg-text-muted)]">Assigned to</span>
                <Select value={activeFilters.assignedTo} onValueChange={handleAssignedFilterChange}>
                  <SelectTrigger className="w-full bg-[var(--tg-surface)] border-[var(--tg-border-soft)] text-sm">
                    <SelectValue placeholder="Assigned to" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--tg-surface)] text-[var(--tg-text-primary)]">
                    <SelectItem value="all">All agents</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {agents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id} disabled={!agent.is_active}>
                        {agent.name} {agent.is_active ? '' : '(inactive)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                handleClearFilters();
              }}
            >
              Clear
            </Button>
            <Button className="flex-1" onClick={() => setFilterSheetOpen(false)}>
              Apply
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </AppShell>
  );
};

const InboxPage = ({ user, onLogout }) => (
  <ChatProvider userRole={user.role} userId={user.id}>
    <InboxContent user={user} onLogout={onLogout} />
  </ChatProvider>
);

export default InboxPage;
